/**
 * Reading a template's sample payload as a field source: the Fields panel's tree and the block
 * setup dialog's list/column/field pickers both walk it with these.
 */
import { MAX_LOOP_SCAFFOLD_FIELDS } from '../cdm/fieldInsert'

export const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
export const isScalar = (v: unknown) => !isObject(v) && !Array.isArray(v)

/** An array's item fields for a loop scaffold: the first item's first-level scalars, or null for primitives. */
export function arrayItemFields(arr: unknown[]): string[] | null {
  const first = arr.find(x => x !== null && x !== undefined)
  if (first === undefined || isScalar(first)) return null
  if (!isObject(first)) return []
  return Object.keys(first).filter(k => isScalar(first[k])).slice(0, MAX_LOOP_SCAFFOLD_FIELDS)
}

export interface SampleList { path: string; items: unknown[] }
export interface SampleScalar { path: string; value: unknown }

/** Every array reachable through objects (not through other arrays), by dotted path. */
export function sampleLists(data: unknown, prefix = ''): SampleList[] {
  if (!isObject(data)) return []
  return Object.entries(data).flatMap(([k, v]) => {
    const path = prefix ? `${prefix}.${k}` : k
    if (Array.isArray(v)) return [{ path, items: v }]
    return isObject(v) ? sampleLists(v, path) : []
  })
}

/** Every scalar reachable through objects (not through arrays), by dotted path. */
export function sampleScalars(data: unknown, prefix = ''): SampleScalar[] {
  if (!isObject(data)) return []
  return Object.entries(data).flatMap(([k, v]) => {
    const path = prefix ? `${prefix}.${k}` : k
    if (isScalar(v)) return [{ path, value: v }]
    return isObject(v) ? sampleScalars(v, path) : []
  })
}

/** The first item's first-level scalar keys with their values, all of them (no scaffold cap). */
export function itemScalars(items: unknown[]): SampleScalar[] {
  const first = items.find(isObject)
  return first ? Object.entries(first).filter(([, v]) => isScalar(v)).map(([path, value]) => ({ path, value })) : []
}
